import type {CaptionErrorByAccountIds, DesignSelection, TitleErrorByAccountIds} from '@Components/social-media/social-media.types';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import type {AccountMediaTypeInformation, SocialAccount} from '@Components/social-media/account.vo';
import type {MediaTypePublishingParams, SocialMediaAccountId, SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {GraphicsMetaMapType, SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import {CaptionErrorTypes} from '@Components/social-media-wizard/social-media-wizard.types';
import {getPlatformMediaTypeLabel} from '@Libraries/social-media-platform.library';
import {getNumPublishingParams} from '@Panels/social-posts-dialog-panel/social-posts-dialog-panel.reducer';

export const getCaptionFromPublishingParams = (
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: SocialMediaAccountId,
  platformMediaType: PlatformMediaType
): string => {
  const paramsForMediaType = publishingParams[accountId]?.[platformMediaType];

  if (paramsForMediaType && 'caption' in paramsForMediaType && typeof paramsForMediaType.caption === 'string') {
    return paramsForMediaType.caption;
  }

  return '';
};

export const getCaptionWithPlaceholder = (
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: SocialMediaAccountId,
  platformMediaType: PlatformMediaType
): string => {
  return getCaptionFromPublishingParams(publishingParams, accountId, platformMediaType) || window.i18next.t('pmwjs_caption_placeholder');
};

export const getCaptionErrorAccountId = (accountIdsToPublishTo: SocialMediaAccountId[], captionErrors: CaptionErrorByAccountIds): number | null => {
  const nAccounts = accountIdsToPublishTo.length;
  for (let i = 0; i < nAccounts; i++) {
    if (captionErrors[accountIdsToPublishTo[i]] && captionErrors[accountIdsToPublishTo[i]] !== CaptionErrorTypes.NO_ERROR) {
      return accountIdsToPublishTo[i];
    }
  }

  return null;
};

export const getTitleErrorAccountId = (accountIdsToPublishTo: SocialMediaAccountId[], titleErrors: TitleErrorByAccountIds): number | null => {
  const nAccounts = accountIdsToPublishTo.length;
  for (let i = 0; i < nAccounts; i++) {
    if (titleErrors[accountIdsToPublishTo[i]] && titleErrors[accountIdsToPublishTo[i]] !== null) {
      return accountIdsToPublishTo[i];
    }
  }

  return null;
};

export const mapIndexToAccountPublishingParams = (socialPost: SocialPost, index: number): AccountMediaTypeInformation => {
  const allPublishingParams = socialPost.publishingParams;

  const correctedIndex = index === -1 ? 0 : index;
  let accountIndex = 0;

  for (let i = 0; i < getNumPublishingParams(socialPost); ) {
    const account = socialPost.accounts[accountIndex];

    if (allPublishingParams[account.id].POST) {
      if (i === correctedIndex) {
        return {
          account,
          platformMediaType: PlatformMediaType.POST,
        };
      }

      i += 1;
    }

    if (allPublishingParams[account.id].REELS) {
      if (i === correctedIndex) {
        return {
          account,
          platformMediaType: PlatformMediaType.REELS,
        };
      }

      i += 1;
    }

    if (allPublishingParams[account.id].STORIES) {
      if (i === correctedIndex) {
        return {
          account,
          platformMediaType: PlatformMediaType.STORIES,
        };
      }

      i += 1;
    }

    accountIndex += 1;
  }

  console.error('No publishing params for index found');
  return {
    account: socialPost.accounts[0],
    platformMediaType: PlatformMediaType.POST,
  };
};

export const getPublishUrlFromPublishParams = (publishingParams: SocialMediaPublishingParams | null): string => {
  return publishingParams?.publishedURL ?? '';
};

export const isPublishingAsPost = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return publishingParamsToCheck[accountId].POST !== null;
};
export const isPublishingAsStory = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return publishingParamsToCheck[accountId].STORIES !== null;
};

export const isPublishingAsReel = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return publishingParamsToCheck[accountId].REELS !== null;
};

export const isPublishingAsPostOrReel = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return isPublishingAsPost(publishingParamsToCheck, accountId) || isPublishingAsReel(publishingParamsToCheck, accountId);
};

export const isPublishingAsPostAndStory = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return isPublishingAsPostOrReel(publishingParamsToCheck, accountId) && isPublishingAsStory(publishingParamsToCheck, accountId);
};

export const getAccountMediaTypeSubtitleSuffix = (
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  account: SocialAccount,
  platformMediaType: PlatformMediaType
): string => {
  if (publishingParams[account.id] === undefined || !isPublishingAsPostAndStory(publishingParams, account.id)) {
    return '';
  }

  return getPlatformMediaTypeLabel(platformMediaType);
};

export const isPublishingAsStoryOnly = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return !isPublishingAsPostOrReel(publishingParamsToCheck, accountId) && isPublishingAsStory(publishingParamsToCheck, accountId);
};

export const isPublishingAsAPostOrReelOnly = (publishingParamsToCheck: Record<SocialMediaAccountId, MediaTypePublishingParams>, accountId: number): boolean => {
  return isPublishingAsPostOrReel(publishingParamsToCheck, accountId) && !isPublishingAsStory(publishingParamsToCheck, accountId);
};

export const hasPublishingTransitionedFromBothToPostOrReelOnly = (
  oldPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  newPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: number
): boolean => {
  const wasPublishingAsPostOrReel = isPublishingAsPostOrReel(oldPublishingParams, accountId);
  const wasPublishingAsStory = isPublishingAsStory(oldPublishingParams, accountId);

  const wasPublishingAsBoth = wasPublishingAsStory && wasPublishingAsPostOrReel;

  if (!wasPublishingAsBoth) {
    return false;
  }

  const isPublishingToPostOrReelOnly = isPublishingAsAPostOrReelOnly(newPublishingParams, accountId);

  return wasPublishingAsBoth && isPublishingToPostOrReelOnly;
};

export const hasPublishingTransitionedFromPostOrReelOnlyToBoth = (
  oldPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  newPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: number
): boolean => {
  const wasPublishingAsPostOrReelOnly = isPublishingAsAPostOrReelOnly(oldPublishingParams, accountId);

  if (!wasPublishingAsPostOrReelOnly) {
    return false;
  }

  const isPublishingToBoth = isPublishingAsStory(newPublishingParams, accountId) && isPublishingAsPostOrReel(newPublishingParams, accountId);

  return wasPublishingAsPostOrReelOnly && isPublishingToBoth;
};

export const hasPublishingTransitionedFromStoryOnlyToBoth = (
  oldPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  newPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: number
): boolean => {
  const wasPublishingAsStoryOnly = isPublishingAsStoryOnly(oldPublishingParams, accountId);

  if (!wasPublishingAsStoryOnly) {
    return false;
  }

  const isPublishingToBoth = isPublishingAsStory(newPublishingParams, accountId) && isPublishingAsPostOrReel(newPublishingParams, accountId);

  return wasPublishingAsStoryOnly && isPublishingToBoth;
};

export const hasPublishingTransitionedFromBothToStoryOnly = (
  oldPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  newPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: number
): boolean => {
  const wasPublishingAsPostOrReel = isPublishingAsPostOrReel(oldPublishingParams, accountId);
  const wasPublishingAsStory = isPublishingAsStory(oldPublishingParams, accountId);

  const wasPublishingAsBoth = wasPublishingAsStory && wasPublishingAsPostOrReel;

  if (!wasPublishingAsBoth) {
    return false;
  }

  const isPublishingStoryOnly = isPublishingAsStoryOnly(newPublishingParams, accountId);

  return wasPublishingAsBoth && isPublishingStoryOnly;
};

export const havePublishingParamsChangedForAccountWithId = (
  oldPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  newPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>,
  accountId: number
): boolean => {
  let checkPost = false;
  let checkReel = false;
  let checkStory = false;

  if ((oldPublishingParams[accountId].POST && newPublishingParams[accountId].POST) || (!oldPublishingParams[accountId].POST && !newPublishingParams[accountId].POST)) {
    checkPost = true;
  }

  if ((oldPublishingParams[accountId].STORIES && newPublishingParams[accountId].STORIES) || (!oldPublishingParams[accountId].STORIES && !newPublishingParams[accountId].STORIES)) {
    checkStory = true;
  }

  if ((oldPublishingParams[accountId].REELS && newPublishingParams[accountId].REELS) || (!oldPublishingParams[accountId].REELS && !newPublishingParams[accountId].REELS)) {
    checkReel = true;
  }

  return !(!checkReel && !checkPost && !checkStory);
};

export const isCaptionTagsHighlightingEnabled = (accountType: SocialPlatform): boolean => {
  switch (accountType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_GROUP:
    case SocialPlatform.TWITTER:
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
    case SocialPlatform.TIKTOK:
    case SocialPlatform.YOUTUBE:
    case SocialPlatform.INSTAGRAM:
    case SocialPlatform.THREADS:
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
    case SocialPlatform.PINTEREST:
      return true;
    default:
      return false;
  }
};

export const getDefaultPublishParamsForMediaType = (account: SocialAccount, publishingParams: SocialMediaPublishingParams, isInstagramReel: boolean): MediaTypePublishingParams => {
  if (account.type === SocialPlatform.INSTAGRAM && isInstagramReel) {
    return {
      [PlatformMediaType.REELS]: publishingParams,
      [PlatformMediaType.POST]: null,
      [PlatformMediaType.STORIES]: null,
    };
  }

  return {
    [PlatformMediaType.POST]: publishingParams,
    [PlatformMediaType.REELS]: null,
    [PlatformMediaType.STORIES]: null,
  };
};

export const getNonStoryPublishingParams = (platformMediaTypeParams: MediaTypePublishingParams): SocialMediaPublishingParams | null => {
  if (platformMediaTypeParams.REELS) {
    return platformMediaTypeParams.REELS;
  }

  return platformMediaTypeParams.POST;
};

export const getStoryPublishingParams = (platformMediaTypeParams: MediaTypePublishingParams): SocialMediaPublishingParams | null => {
  return platformMediaTypeParams.STORIES;
};

export const getNonStoryPlatformMediaType = (platformMediaTypeParams: MediaTypePublishingParams): PlatformMediaType | null => {
  if (platformMediaTypeParams.REELS) {
    return PlatformMediaType.REELS;
  }

  if (!platformMediaTypeParams.POST) {
    return null;
  }

  return PlatformMediaType.POST;
};

/**
 * Returns every design reference in a graphics-meta map, in the map's own key order (which matches how
 * the preview loading map is seeded). The graphics-meta map is persisted self-contained in
 * `publishing_params`, so it is the durable source of truth for a published post's media even after its
 * source poster is gone.
 */
export const getAllDesignsFromGraphicsMetaMap = (graphicsMetaMap: GraphicsMetaMapType): DesignSelection[] => {
  const designs: DesignSelection[] = [];

  Object.keys(graphicsMetaMap).forEach((posterHashedId) => {
    Object.keys(graphicsMetaMap[posterHashedId]).forEach((pageHashedId) => {
      designs.push({posterHashedId, pageHashedId});
    });
  });

  return designs;
};

/**
 * Returns the first design reference present in a graphics-meta map, or null when the map is empty.
 */
export const getFirstDesignFromGraphicsMetaMap = (graphicsMetaMap: GraphicsMetaMapType): DesignSelection | null => {
  return getAllDesignsFromGraphicsMetaMap(graphicsMetaMap)[0] ?? null;
};

export const isDesignInGraphicsMetaMap = ({posterHashedId, pageHashedId}: DesignSelection, graphicsMetaMap: GraphicsMetaMapType): boolean => {
  return graphicsMetaMap[posterHashedId]?.[pageHashedId] !== undefined;
};

/**
 * Resolves the design to render for a post preview. Prefers the explicit `designs` selection, but
 * falls back to the graphics-meta map when that selection is missing or not represented in the map.
 * A published post whose source poster was later deleted comes back with an empty `designs` array
 * while its graphics-meta map survives, so the map is the reliable reference in that case.
 */
export const getRenderableDesignFromGraphicsMetaMap = (designs: DesignSelection[], graphicsMetaMap: GraphicsMetaMapType): DesignSelection | null => {
  const [firstDesign] = designs;
  if (firstDesign && isDesignInGraphicsMetaMap(firstDesign, graphicsMetaMap)) {
    return firstDesign;
  }

  return getFirstDesignFromGraphicsMetaMap(graphicsMetaMap);
};

/**
 * Returns a still-image URL from a post's durable publishing params, for use as a grid thumbnail when
 * the poster-derived preview URLs are unavailable (source design deleted after publishing). Prefers a
 * video cover, then a non-video graphic. Returns undefined when the only durable media is a coverless
 * video (no still frame exists to show).
 */
export const getFallbackPreviewImageUrlFromPublishingParams = (publishingParams: SocialPost['publishingParams']): string | undefined => {
  const graphicMetas = Object.values(publishingParams)
    .flatMap((mediaTypeParams) => Object.values(mediaTypeParams))
    .filter((params): params is SocialMediaPublishingParams => params !== null)
    .flatMap((params) => Object.values(params.graphicsMetaMap))
    .flatMap((pageGraphicsMetaMap) => Object.values(pageGraphicsMetaMap));

  const coverUrl = graphicMetas.find((graphicMeta) => Boolean(graphicMeta.cover?.coverUrl))?.cover?.coverUrl;
  if (coverUrl !== undefined) {
    return coverUrl;
  }

  return graphicMetas.find((graphicMeta) => !graphicMeta.isVideo && graphicMeta.graphicURL !== '')?.graphicURL;
};
